import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useToast } from "../../services/utils";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Stack,
  Grid,
  IconButton,
  InputBase,
  Tooltip,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import DataTable from "../../components/DataGrids/DataGrid";
import FlexBetween from "../../components/FlexBetween";
import AutoCompleteDomainList from "../../components/Molecules/Domain/AutoCompleteDomainList";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import {
  deleteResource,
  reactivateResource,
} from "../../services/resourcesService";
import { Search } from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import useFetchLinkedServices from "../../hooks/useFetchLinkedServices";
import useFetchPaginatedResources from "../../hooks/useFetchPaginatedResources";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { paginatedResponseFormat } from "../../services/constants";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import {
  IconReportsSvg,
  IconAudit,
  IconExportIconBlue,
  IconEyeBase,
  IconEditBase,
  IconDeleteBlueSvg,
  IconValidateResourceBase,
  IconReactivateSvg,
  IconCrossCircleIconSvg,
  IconCheckCircleIconGreenSvg,
} from "../../common/utils/icons";
import {
  downloadResourceExportFile,
  getCurrentDateAndLast7Days,
  getFormattedDateTime,
} from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import AssociateEntitiesDialog from "../../components/Dialogs/AssociateEntitiesDialog";
import CloneResourceWithResourceColumnDialog from "../../components/Dialogs/CloneResourceWithResourceColumnDialog";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { useAuth } from "../../contexts/AuthContext";
import {
  useAuthTagContext,
  useSystemContext,
} from "../../contexts/TagAndSystemProvider";
import useFetchAllSystem from "../../hooks/useFetchAllSystem";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import SystemFilterDropdown from "../../components/Molecules/System/SystemFilterDropdown";
import useFetchActiveAuthTagsList from "../../hooks/useFetchActiveAuthTagsList";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
// import AuditDialog from "../../components/Dialogs/DiffAceEditorDialog";

// Centralized Filter Configuration for Resource List
const RESOURCE_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    domain_id: null,
    query_name: "",
    system_id: null,
    status_filter: "Active",
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: ["domain_id", "system_id"],
    STRING_FIELDS: ["query_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const Resource: React.FC = () => {
  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  //for navigation & parameters
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { domainId, id } = useParams();
  const { setAllAuthTagsData } = useAuthTagContext();

  // Permission checks for resource actions
  const canViewResource = hasPermission("resource", "view");
  const canAddResource = hasPermission("resource", "add");
  const canEditResource = hasPermission("resource", "edit");
  const canDeleteResource = hasPermission("resource", "delete");
  const canValidationResult = hasPermission("validation_result", "view");
  const canComparisonResult = hasPermission("rule_result", "view");
  const canValidateResource = hasPermission("resource", "validate");

  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSystemLoading, setIsSystemLoading] = useState<boolean>(true);
  // const [openAuditDialog, setOpenAuditDialog] = useState(false);
  const [currentDomainId, setCurrentDomainId] = useState<
    string | number | null
  >(Number(domainId) || "");
  const [resourcesData, setResourcesData] = useState<any>(
    paginatedResponseFormat
  );

  // Ref to track if this is the initial mount
  const isInitialMount = useRef(true);

  // Ref to track the previous domain ID to detect actual domain changes
  const prevDomainIdRef = useRef(currentDomainId);

  // Ref to prevent circular updates between URL and state
  const isUpdatingFromURL = useRef(false);

  // Initialize filter state from URL on mount
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    // Use centralized default state
    const defaultFilterState = { ...RESOURCE_FILTER_CONFIG.DEFAULT_STATE };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Initialize domain_id from URL parameter (domainId from useParams)
    if (domainId && domainId !== "all" && !isNaN(Number(domainId))) {
      urlFilters.domain_id = Number(domainId);
    }

    // Handle ID fields (convert to number if present)
    RESOURCE_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value && !isNaN(Number(value))) {
        urlFilters[field] = Number(value);
      }
    });

    // Handle string fields
    RESOURCE_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        urlFilters[field] = value;
      }
    });

    return { ...defaultFilterState, ...urlFilters };
  });

  // Fetch only the specific auth tags needed for this resource (optimized approach)
  const [authTagsData] = useFetchActiveAuthTagsList({
    setIsLoading,
    enabled: true,
  });

  // Update context state when auth tags data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (authTagsData && Array.isArray(authTagsData)) {
      setAllAuthTagsData(authTagsData);
    }
  }, [authTagsData, setAllAuthTagsData]);

  // Helper function to get filter state from URL (for use in effects)
  const getFilterStateFromURL = useCallback(() => {
    // Use centralized default state
    const defaultFilterState = { ...RESOURCE_FILTER_CONFIG.DEFAULT_STATE };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Initialize domain_id from URL parameter (domainId from useParams)
    if (domainId && domainId !== "all" && !isNaN(Number(domainId))) {
      urlFilters.domain_id = Number(domainId);
    }

    // Handle ID fields (convert to number if present)
    RESOURCE_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value && !isNaN(Number(value))) {
        urlFilters[field] = Number(value);
      }
    });

    // Handle string fields
    RESOURCE_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        urlFilters[field] = value;
      }
    });

    return { ...defaultFilterState, ...urlFilters };
  }, [searchParams, domainId]);

  // Helper function to serialize filter data to URL parameters
  const serializeFiltersToURL = (filters: any) => {
    const newParams = new URLSearchParams(searchParams);

    // Use centralized field configuration, but exclude domain_id since it's in the route path
    const fieldsToSerialize = RESOURCE_FILTER_CONFIG.URL_FIELDS.filter(
      (field) => field !== "domain_id"
    );

    // Remove all existing filter parameters first
    fieldsToSerialize.forEach((field) => {
      newParams.delete(field);
    });

    // Add non-empty filter values to URL
    fieldsToSerialize.forEach((field) => {
      const value = filters[field];
      if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "string") {
          newParams.set(field, value);
        } else {
          newParams.set(field, String(value));
        }
      }
    });

    return newParams;
  };

  // Individual state getters for backward compatibility
  const searchQuery = searchFilterData.query_name;
  const selectedSystemId = searchFilterData.system_id;
  const statusFilter = searchFilterData.status_filter;

  // Debounce search term with default delay
  const debouncedSearchTerm = useDebounce(searchQuery);

  // Determine if we should include search term in API call
  const effectiveSearchTerm =
    debouncedSearchTerm.trim().length >= 3 ? debouncedSearchTerm : "";

  // Convert status filter to API format
  const isActiveParam =
    statusFilter === "Active"
      ? "true"
      : statusFilter === "Inactive"
      ? "false"
      : "all";

  const [fetchedResourcesData] = useFetchPaginatedResources({
    currentDomainId,
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveSearchTerm,
    system_id: selectedSystemId || undefined,
    isActive: isActiveParam,
  });

  // Reset page to 1 when search term or system filter changes
  useEffect(() => {
    if (
      debouncedSearchTerm.trim().length >= 3 ||
      debouncedSearchTerm.trim().length === 0
    ) {
      setPage(1);
    }
  }, [debouncedSearchTerm]);

  // Reset page to 1 when system filter changes
  useEffect(() => {
    setPage(1);
  }, [selectedSystemId]);

  // Reset page to 1 when status filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Sync domain_id from searchFilterData to currentDomainId and navigate
  useEffect(() => {
    const domainIdFromFilter = searchFilterData.domain_id;

    // Only update if domain_id actually changed
    if (domainIdFromFilter !== currentDomainId) {
      setCurrentDomainId(domainIdFromFilter || null);

      // Navigate to the correct route based on domain_id
      if (domainIdFromFilter) {
        navigate(`/resource/${domainIdFromFilter}`, { replace: true });
      } else {
        navigate(`/resource/all`, { replace: true });
      }
    }
  }, [searchFilterData.domain_id]); // Only depend on domain_id to avoid circular updates

  // Update URL when filter state changes
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Serialize current filter state to URL parameters
    const newParams = serializeFiltersToURL(searchFilterData);

    // Only update URL if the serialized params differ from current URL
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData]); // Removed searchParams and setSearchParams from dependencies to prevent circular updates

  // Update filter state when URL changes (for back/forward navigation)
  useEffect(() => {
    // Extract current filter values from URL
    const urlFilterState = getFilterStateFromURL();

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? null
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? null
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes
    if (hasFilterChanges) {
      // Set flag to prevent the URL update effect from running
      isUpdatingFromURL.current = true;
      setSearchFilterData(urlFilterState);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 0);
    }
  }, [getFilterStateFromURL]); // Removed searchParams to prevent circular updates

  // Clear search and system filter when domain changes (but not on initial mount)
  useEffect(() => {
    // Check if domain actually changed (not just initial mount)
    if (prevDomainIdRef.current !== currentDomainId) {
      // Clear all filter parameters from URL
      const newParams = new URLSearchParams();
      setSearchParams(newParams, { replace: true });

      // Reset filter state to defaults (keeping the new domain_id)
      setSearchFilterData({
        ...RESOURCE_FILTER_CONFIG.DEFAULT_STATE,
        domain_id: currentDomainId,
      });

      // Update the ref for next comparison
      prevDomainIdRef.current = currentDomainId;
    }
  }, [currentDomainId, setSearchParams]);

  // Get the setter function from context (following linkedServicesData pattern)
  const { allSystemData, setAllSystemData } = useSystemContext();

  // Fetch system data directly in this component (following linkedServicesData pattern)
  const [systemData] = useFetchAllSystem({
    setIsLoading: setIsSystemLoading,
  });

  // Update context state when system data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (systemData && Array.isArray(systemData)) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);
  // Create a robust system lookup map with fallback handling
  const systemLookupMap = useMemo(() => {
    if (!allSystemData || !Array.isArray(allSystemData)) return {};
    return allSystemData.reduce((acc: any, system: any) => {
      acc[system.id] = system.value;
      return acc;
    }, {});
  }, [allSystemData]);

  // Helper function to get system name with fallback
  const getSystemName = useCallback(
    (systemId: any) => {
      if (!systemId) return "N/A";

      // If system data is still loading, show loading indicator
      if (isSystemLoading) return "Loading...";

      // Try to get system name from lookup map
      const systemName = systemLookupMap[systemId];
      if (systemName) return systemName;

      // Fallback to system ID if name not found
      return `System ${systemId}`;
    },
    [systemLookupMap, isSystemLoading]
  );
  const [cloneResourceDialog, setCloneResourceDialog] =
    useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [resourceToDelete, setResourceToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [linkedServicesData] = useFetchLinkedServices({ setIsLoading });
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [openAssociateEntitiesDialog, setOpenAssociateEntitiesDialog] =
    useState<boolean>(false);
  const [associateEntityId, setAssociateEntityId] = useState<number | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handel events

  const handelOnChangeDomain = (event: any, value: any) => {
    if (value?.id !== undefined) {
      setCurrentDomainId(value?.id);
      navigate(`/resource/${value?.id}`);
      setPage(defaultPage ? parseInt(defaultPage) : 1);
      setPSize(rowsPerPage);
    } else {
      navigate(`/resource/all`);
      setCurrentDomainId(null);
      setFileData(paginatedResponseFormat);
    }
  };
  const handelClickEvent = (url: any) => {
    navigate(url);
  };
  const getLinkedServiceCodebyId = (id: number | string | null | undefined) => {
    const linkedData = linkedServicesData?.find((item: any) => item.id === id);

    return linkedData ? linkedData.name : null;
  };

  const handleAssociateEntitiesDialog = () => {
    setOpenAssociateEntitiesDialog(true);
  };

  // Handle delete resource
  const handleDeleteResource = (resource: any) => {
    setResourceToDelete(resource);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!resourceToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteResource(resourceToDelete.id);
      if (result && result.status === 200) {
        showToast(
          `${resourceToDelete.resource_name} deleted successfully`,
          "success"
        );
        setDeleteConfirmOpen(false);
        setResourceToDelete(null);
        // Update the resources list to remove the deleted resource
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.filter(
            (prevItem: any) => prevItem.id !== resourceToDelete.id
          ),
        }));
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to delete resource", "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate resource
  const handleReactivate = async (id: number, resourceName: string) => {
    setIsLoading(true);
    try {
      await reactivateResource(id);
      showToast(
        `Resource "${resourceName}" reactivated successfully`,
        "success"
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));
    } catch (error) {
      showToast("Failed to reactivate resource", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize column definitions to prevent unnecessary re-renders
  // and ensure they update when helper functions change
  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        minWidth: 20,
        flex: 0,
        renderCell: (params: any) => <></>,
        cellClassName: "no-padding-cell",
        headerClassName: "no-padding-cell",
      },
      {
        field: "id",
        headerName: "ID",
        minWidth: 60,
        groupable: false,
        renderCell: (params: any) => {
          if (params.rowNode.type === "group") {
            return null;
          }
          return <span>{params.id}</span>;
        },
      },

      {
        field: "domain_name",
        headerName: "Domain Name",
        minWidth: 160,
        flex: 1,
        renderCell: (params: any) => {
          const handleClickDomain = (params: any) => {
            navigate(`/domain/view/${params?.row?.domain_id}`);
          };
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
      {
        field: "resource_type",
        headerName: "System",
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => {
          const systemName = getSystemName(params.row.system_id);
          return <LimitChractersWithTooltip value={systemName} />;
        },
      },

      {
        field: "resource_name",
        headerName: "Resource Name",
        minWidth: 160,
        flex: 1,
        groupable: false,
        renderCell: (params: any) => (
          <LimitChractersWithTooltip value={params?.value} />
        ),
      },

      {
        field: "linked_service_id",
        headerName: "Connection Type",
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => {
          const linkedServiceName = getLinkedServiceCodebyId(params.value);
          return (
            <>
              <LimitChractersWithTooltip value={linkedServiceName} />
            </>
          );
        },
      },
      {
        field: "resource_prefix",
        headerName: "Prefix",
        minWidth: 140,
        flex: 1,
        groupable: false,
        renderCell: (params: any) => (
          <LimitChractersWithTooltip value={params?.value} />
        ),
      },
      {
        field: "column_delimiter",
        headerName: "Column Delimiter",
        minWidth: 70,
        groupable: false,
        renderCell: (params: any) => {
          return (
            <>
              <span>
                {params?.row?.additional_properties?.resource_definition
                  ?.type === "local" &&
                  params?.row?.additional_properties?.resource_definition
                    ?.local_definition?.column_delimiter}
              </span>
              <span>
                {params?.row?.additional_properties?.resource_definition
                  ?.type === "blob" &&
                  params?.row?.additional_properties?.resource_definition
                    ?.blob_definition?.column_delimiter}
              </span>
              <span>
                {params?.row?.additional_properties?.resource_definition
                  ?.type === "sftp" &&
                  params?.row?.additional_properties?.resource_definition
                    ?.sftp_definition?.column_delimiter}
              </span>
              <span>
                {params?.row?.additional_properties?.resource_definition
                  ?.type === "sql" && "N/A"}
              </span>
              <span>
                {params?.row?.additional_properties?.resource_definition
                  ?.type === "postgres_sql" && "N/A"}
              </span>
            </>
          );
        },
      },
      {
        field: "modified_by_user",
        headerName: "Modified By",
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value?.username} />;
        },
      },
      {
        field: "updated_on",
        headerName: "Last Modified",
        minWidth: 180,
        flex: 1,
        renderCell: (params: any) => {
          return getFormattedDateTime(params.value);
        },
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params: any) => {
          return params.value ? (
            <Tooltip title="Active" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCheckCircleIconGreenSvg width={21} height={21} />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Inactive" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCrossCircleIconSvg width={21} height={21} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        width: 220, // BUG-39: Increased from 180 to 220 for better spacing of action buttons
        align: "center",
        headerAlign: "center",
        groupable: false,
        renderCell: (params: any) => {
          if (params.rowNode.type === "group") {
            return null;
          }
          const handleDelete = () => {
            handleDeleteResource(params.row);
          };
          const { last7DaysDate } = getCurrentDateAndLast7Days();
          const isActive = params.row.is_active === true;

          return (
            <>
              {/* For active records, show all action buttons */}
              {isActive && (
                <>
                  {canValidateResource && (
                    <Tooltip title="Validate Resource" placement="top" arrow>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/resource/${domainId}/validate-resource/${params.row.id}`
                          )
                        }
                      >
                        <IconValidateResourceBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canViewResource && (
                    <Tooltip title="View Resource" placement="top" arrow>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/resource/${domainId}/view/${params.row.id}`
                          )
                        }
                      >
                        <IconEyeBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canEditResource && (
                    <Tooltip title="Edit Resource" placement="top" arrow>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/resource/${domainId}/edit/${params.row.id}`
                          )
                        }
                      >
                        <IconEditBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  <DropdownMenu
                    dropdownMenuItems={[
                      // Recent validations - requires view permission
                      ...(canValidationResult
                        ? [
                            <IconButton
                              key="recent-validations"
                              onClick={() => {
                                navigate(
                                  `/validation-execution-history?resourceId=${params.row.id}&fromDate=${last7DaysDate}`
                                );
                              }}
                              size="small"
                              className="datagrid-action-btn"
                            >
                              <IconReportsSvg />
                              Recent validations
                            </IconButton>,
                          ]
                        : []),
                      // Audit Resource - requires view permission
                      ...(canViewResource
                        ? [
                            <IconButton
                              key="audit-resource"
                              className="datagrid-action-btn"
                              color="inherit"
                              onClick={() =>
                                navigate(
                                  `/resource/${domainId}/auditResource/${params.row.id}`
                                )
                              }
                            >
                              <IconAudit />
                              Audit Resource
                            </IconButton>,
                          ]
                        : []),
                      // Export - requires view permission
                      ...(canViewResource
                        ? [
                            <IconButton
                              key="export"
                              className="datagrid-action-btn"
                              color="inherit"
                              onClick={() => {
                                setAssociateEntityId(params.row.id);
                                handleAssociateEntitiesDialog();
                              }}
                            >
                              <IconExportIconBlue />
                              Export
                            </IconButton>,
                          ]
                        : []),
                      // Delete Resource - requires delete permission
                      ...(canDeleteResource
                        ? [
                            <IconButton
                              key="delete-resource"
                              className="datagrid-action-btn"
                              color="inherit"
                              onClick={() => handleDelete()}
                            >
                              <IconDeleteBlueSvg />
                              Delete Resource
                            </IconButton>,
                          ]
                        : []),
                    ]}
                  />
                </>
              )}

              {/* For inactive records, show ONLY Reactivate button */}
              {!isActive && (
                <Tooltip title="Reactivate Resource" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      handleReactivate(params.row.id, params.row.resource_name)
                    }
                  >
                    <IconReactivateSvg />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    ],
    [
      getSystemName,
      getLinkedServiceCodebyId,
      canViewResource,
      canEditResource,
      canDeleteResource,
      canValidationResult,
      canComparisonResult,
      canAddResource,
      domainId,
      navigate,
      showToast,
      fileData,
      handleAssociateEntitiesDialog,
      setAssociateEntityId,
      setCloneResourceDialog,
    ]
  );

  // for update resource list data
  useEffect(() => {
    if (fetchedResourcesData) {
      setFileData(fetchedResourcesData);
      setResourcesData(fetchedResourcesData);
    }
  }, [fetchedResourcesData]);

  const handleSearchInputChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchFilterData((prev: any) => ({
      ...prev,
      search: e.target.value as string,
    }));
  };

  const handleSystemFilterChange = (_event: any, value: any) => {
    setSearchFilterData((prev: any) => ({
      ...prev,
      system_id: value?.id || null,
    }));
  };

  // const handleSearchSubmit = (e: { preventDefault: () => void }) => {
  //   e.preventDefault();
  //   // Filter domains based on the searchQuery
  //   const filteredResource = resourcesData?.items?.filter((resource: any) => {
  //     return resource.resource_name
  //       .toLowerCase()
  //       .includes(searchQuery.toLowerCase());
  //   });
  //   // You can set the filtered domains as a result in your component state
  //   setFileData(filteredResource);
  // };

  const filteredData = useMemo(() => {
    return fileData?.items?.filter((report: any, index: number) => {
      // report.id = index;
      return report.resource_name
        .toLowerCase()
        .includes(searchQuery?.toLowerCase());
    });
  }, [fileData?.items, searchQuery]);

  return (
    <Box>
      {(isLoading || isDownloadLoading || isSystemLoading) && (
        <Loader isLoading={isLoading || isDownloadLoading || isSystemLoading} />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Resource"
        dailogDescription={`Are you sure you want to delete resource "${resourceToDelete?.resource_name}"?`}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={[
                "domain_list",
                "system_id",
                "query_name",
                "status_filter",
              ]}
              primaryFields={["domain_list", "query_name"]}
              fieldRequirements={{
                defaultStatusFilter: "Active",
                queryNamePlaceholder: "Resource name",
              }}
            />
          </Grid>
          <Grid item display={"flex"} justifyContent={"flex-end"}>
            <Stack>
              <Box sx={{ display: "flex", columnGap: 2 }}>
                {canAddResource && (
                  <Button
                    className="btn-orange"
                    onClick={handleClick}
                    variant="contained"
                    sx={{ margin: "0px" }}
                  >
                    <AddSharpIcon sx={{ marginRight: "4px" }} />
                    Resource
                  </Button>
                )}
                {canAddResource && (
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    sx={{
                      marginTop: "38px",
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        navigate(`/resource/add`, {
                          state: {
                            resource_type: "new_resource",
                          },
                        });

                        //handelClickEvent("/resource/add");
                        setAnchorEl(null);
                      }}
                    >
                      New Resource
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        //handelClickEvent("/resource/add?type=sample");

                        navigate(`/resource/add`, {
                          state: {
                            resource_type: "sample",
                          },
                        });

                        setAnchorEl(null);
                      }}
                    >
                      New Resource using Sample data
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setCloneResourceDialog(true);
                        setAnchorEl(null);
                      }}
                    >
                      Clone Resource
                    </MenuItem>
                  </Menu>
                )}
                {/* <ButtonComponent
            className="btn-orange"
            handelClickEvent={() => handelClickEvent("/resource/add")}
          >
            <AddSharpIcon sx={{ marginRight: "4px" }} /> Resource
          </ButtonComponent> */}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      {/* import data table for resource list  */}

      <FlexBetween>
        <DataTable
          dataRows={fileData?.items || []}
          dataColumns={columns || []}
          loading={isLoading}
          dataListTitle="Resource List"
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={362}
        />
      </FlexBetween>

      {/* <AuditDialog
        openAuditDialog={openAuditDialog}
        setOpenAuditDialog={setOpenAuditDialog}
      /> */}
      <AssociateEntitiesDialog
        openAssociateEntitiesDialog={openAssociateEntitiesDialog}
        setOpenAssociateEntitiesDialog={setOpenAssociateEntitiesDialog}
        associateEntityId={associateEntityId}
        downloadExportFile={downloadResourceExportFile}
        setIsDownloadLoading={setIsDownloadLoading}
        header={"Resource"}
      />
      {canAddResource && (
        <CloneResourceWithResourceColumnDialog
          cloneResourceDialog={cloneResourceDialog}
          setCloneResourceDialog={setCloneResourceDialog}
        />
      )}
    </Box>
  );
};

export default Resource;
